// externals
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Box } from '@interstate/components/Box';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import { SelectInput } from '@interstate/components/SelectInput';
import { TextInput } from '@interstate/components/TextInput';
import { Switch } from '@interstate/components/Switch';
import { Typography } from '@interstate/components/Typography';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';

// components
import LabelWithToolTipIcon from '../form/LabelWithToolTipIcon';
import IconWithToolTip from '../form/IconWithToolTip';

// store
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';

// types
import type { StateTree } from '@makemydeal/dr-dash-types';

// constants
import { MANUAL_OVERRIDE_TOOLTIP } from '../../constants';

// styles
import { InfoRow, InfoText } from './EditProductInformation.style';

const EMPTY_PROVIDERS: string[] = [];
const EMPTY_PROVIDER_OPTIONS: { value: string; label: string }[] = [];
const PROVIDER_NAME_MAX_LENGTH = 50;

export type ProviderFieldProps = {
    productCode?: string;
    productCategoryCode?: string;
    productProviderName?: string;
    onProviderChange: (provider: string) => void;
    onValidationError?: (hasError: boolean) => void;
    onOverrideToggle?: (enabled: boolean) => void;
};

const ProviderField = ({
    productCode,
    productCategoryCode,
    productProviderName,
    onProviderChange,
    onValidationError,
    onOverrideToggle
}: ProviderFieldProps) => {
    const theme = useInterstateTheme();
    const normalizedCategoryCode = productCategoryCode?.toUpperCase();

    const providers = useSelector((state: StateTree) =>
        normalizedCategoryCode ? vehicleProtectionSelectors.getVppCategoryProviders(state, normalizedCategoryCode) : EMPTY_PROVIDERS
    );

    const providerOptions = useSelector((state: StateTree) =>
        normalizedCategoryCode
            ? vehicleProtectionSelectors.getVppCategoryProviderOptions(state, normalizedCategoryCode)
            : EMPTY_PROVIDER_OPTIONS
    );

    const originalProviderName = useSelector((state: StateTree) =>
        vehicleProtectionSelectors.getVppOriginalProviderName(state, productCode)
    );

    const providerName = productProviderName ?? '';

    const isProviderOverriddenFromSelector = useSelector((state: StateTree) =>
        vehicleProtectionSelectors.isProductProviderNameOverridden(state, productCode)
    );
    const isTermFieldOverriddenFromSelector = useSelector((state: StateTree) =>
        vehicleProtectionSelectors.isProductTermFieldOverridden(state, productCode)
    );

    const contractApiErroredVPP = useSelector(vehicleProtectionSelectors.getContractApiErroredVPP);
    const isContractApiErroredProduct = Boolean(
        productCode &&
            contractApiErroredVPP &&
            productCode === contractApiErroredVPP.productCode &&
            (contractApiErroredVPP.hasApiMilesError ||
                contractApiErroredVPP.hasApiTermsError ||
                contractApiErroredVPP.hasApiProviderNameError)
    );

    // The override switch governs both the provider and the term fields, so it should default ON when either the
    // provider OR any term field is overridden. It also defaults ON for the product whose coverage term the contract
    // API flagged, because the manual term inputs the user has to fix only render while the switch is on.
    const isOverrideOn = useMemo(
        () => isProviderOverriddenFromSelector || isTermFieldOverriddenFromSelector || isContractApiErroredProduct,
        [isProviderOverriddenFromSelector, isTermFieldOverriddenFromSelector, isContractApiErroredProduct]
    );

    const initialOverrideText = isOverrideOn ? providerName : '';
    const [isOverrideEnabled, setIsOverrideEnabled] = useState(isOverrideOn);
    const [overrideText, setOverrideText] = useState(initialOverrideText);
    // Tracks the original provider name that `overrideText` was authored against, so we can decide
    // whether the saved override text is still relevant if the user toggles the override off and back on.
    const overrideTextProvider = useRef<string | undefined>(initialOverrideText ? originalProviderName : undefined);

    // When the drawer swaps to a different product (e.g. user picks another provider from the dropdown),
    // re-sync local override state from the newly selected product. `useState` only seeds on mount, so
    // without this effect the toggle / text input would keep stale values from the prior product.
    const lastSyncedProductCodeRef = useRef(productCode);
    useEffect(() => {
        if (lastSyncedProductCodeRef.current === productCode) return;
        lastSyncedProductCodeRef.current = productCode;
        setIsOverrideEnabled(isOverrideOn);
        setOverrideText(isOverrideOn ? providerName : '');
        overrideTextProvider.current = isOverrideOn ? originalProviderName : undefined;
    }, [productCode, isOverrideOn, providerName, originalProviderName]);

    // Report the override switch state upward so sibling fields (e.g. term fields) can mirror it.
    useEffect(() => {
        onOverrideToggle?.(isOverrideEnabled);
    }, [isOverrideEnabled, onOverrideToggle]);

    const trimmedOverrideLength = overrideText.trim().length;
    const hasEmptyOverrideError = isOverrideEnabled && trimmedOverrideLength === 0;
    const hasTooLongOverrideError = isOverrideEnabled && trimmedOverrideLength > PROVIDER_NAME_MAX_LENGTH;
    const hasOverrideError = hasEmptyOverrideError || hasTooLongOverrideError;
    const overrideErrorMessage = hasEmptyOverrideError
        ? 'Provider name is required.'
        : hasTooLongOverrideError
        ? `Provider name must be ${PROVIDER_NAME_MAX_LENGTH} characters or fewer.`
        : undefined;

    useEffect(() => {
        onValidationError?.(hasOverrideError);
    }, [hasOverrideError, onValidationError]);

    const handleProviderSelect = useCallback(
        (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
            onProviderChange(event.target.value as string);
        },
        [onProviderChange]
    );

    const handleOverrideTextChange = useCallback(
        (event: InterstateOnChangeEvent<string>) => {
            const val = event.target.value;
            setOverrideText(val);
            overrideTextProvider.current = originalProviderName;
            onProviderChange(val);
        },
        [onProviderChange, originalProviderName]
    );

    const handleOverrideToggle = useCallback(() => {
        const turningOn = !isOverrideEnabled;
        if (turningOn) {
            const shouldReseed = !overrideText || overrideTextProvider.current !== originalProviderName;
            const nextText = shouldReseed ? providerName : overrideText;
            if (shouldReseed) {
                setOverrideText(providerName);
                overrideTextProvider.current = originalProviderName;
            }
            onProviderChange(nextText);
        } else if (originalProviderName) {
            onProviderChange(originalProviderName);
        }
        setIsOverrideEnabled(turningOn);
    }, [isOverrideEnabled, originalProviderName, onProviderChange, providerName, overrideText]);

    if (
        !providers.length &&
        !(
            contractApiErroredVPP &&
            productCode === contractApiErroredVPP.productCode &&
            contractApiErroredVPP.hasApiProviderNameError
        )
    ) {
        return (
            <InfoRow data-testid="product-provider" theme={theme}>
                <InfoText theme={theme}>Provider: </InfoText>
                {providerName || '--'}
            </InfoRow>
        );
    }

    const selectOptions = // Options come from the store, so the currently-edited product's option reflects its committed
        // (possibly overridden) name. Swap that option's label with the drawer's in-flight `providerName`
        // so toggling override on/off in the drawer updates the displayed label without waiting for save.
        providerOptions.map((opt) => (opt.value === originalProviderName ? { ...opt, label: providerName || opt.label } : opt));

    return (
        <Box marginBottom="0.5rem">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <LabelWithToolTipIcon
                    name="productProviderName"
                    title="Provider"
                    toolTipContent="Once you change the Provider, we'll pre-select its most profitable Plan."
                />
                <Box display="flex" alignItems="center">
                    <Typography variant="label-md" color="sem.color.on-surface.muted" tag="span">
                        Enable override
                    </Typography>
                    <Box sx={{ zIndex: 2, display: 'flex', alignItems: 'center' }}>
                        <IconWithToolTip name="vppFieldsOverride" toolTipContent={MANUAL_OVERRIDE_TOOLTIP} position="top" />
                    </Box>
                    <Switch
                        name="vppFieldsOverrideToggle"
                        data-testid="vpp-fields-override-toggle"
                        checked={isOverrideEnabled}
                        onClick={handleOverrideToggle}
                        sx={{ marginLeft: '0.5rem' }}
                    />
                </Box>
            </Box>
            {isOverrideEnabled ? (
                <Box marginTop="0.7rem">
                    <TextInput
                        data-testid="edit-product-provider-text"
                        displayLabel={false}
                        name="productProviderName"
                        value={overrideText}
                        hasError={hasOverrideError}
                        errorMessage={overrideErrorMessage}
                        onChange={handleOverrideTextChange}
                    />
                </Box>
            ) : (
                <Box marginTop="0.7rem">
                    <SelectInput
                        data-testid="edit-product-provider"
                        displayLabel={false}
                        name="productProviderName"
                        onChange={handleProviderSelect}
                        value={originalProviderName ?? providerName}
                        options={selectOptions}
                        displayDeselectOption={false}
                    />
                </Box>
            )}
        </Box>
    );
};

export default ProviderField;
